import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '../services/api/auth.service';
import tokenRefreshManager from '../utils/tokenRefreshManager';
import authService from '../services/api/auth.service';

// Auth state interface
interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

/**
 * Authentication Store using Zustand
 * Persists auth state to localStorage
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      // Actions
      setUser: (user) => 
        set((state) => ({ 
          ...state, 
          user,
          isAuthenticated: !!user && !!state.token 
        })),

      setToken: (token) => {
        // Also update localStorage for API service
        if (token) {
          localStorage.setItem('auth_token', token);
        } else {
          localStorage.removeItem('auth_token');
        }
        
        set((state) => ({ 
          ...state, 
          token,
          isAuthenticated: !!token && !!state.user 
        }));
      },

      setLoading: (isLoading) => 
        set((state) => ({ 
          ...state, 
          isLoading 
        })),

      login: (user, token) => {
        // Update localStorage for API service
        localStorage.setItem('auth_token', token);
        
        // ✅ Start JWT auto-refresh when user logs in
        tokenRefreshManager.start();
        
        set(() => ({
          user,
          token,
          isAuthenticated: true,
          isLoading: false
        }));
      },

      logout: () => {
        // Clear localStorage for API service
        localStorage.removeItem('auth_token');
        
        // ✅ Stop JWT auto-refresh when user logs out
        tokenRefreshManager.stop();
        
        set(() => ({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        }));
      },

      updateUser: (userData) => 
        set((state) => ({
          ...state,
          user: state.user ? { ...state.user, ...userData } : null
        })),
    }),
    {
      name: 'auth-storage', // Storage key
      storage: createJSONStorage(() => localStorage), // Use localStorage
      // Persist all auth data
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      // Handle rehydration
      onRehydrateStorage: () => (state) => {
        if (state?.token && state?.user) {
          // Ensure localStorage is also updated for API service
          localStorage.setItem('auth_token', state.token);
          
          // ✅ Start JWT auto-refresh on app reload
          tokenRefreshManager.start();

          // 🔄 Silent refresh to replace revoked/rotated tokens when user revisits the app
          authService
            .refreshToken()
            .then((result) => {
              if (result.success && result.data?.token) {
                useAuthStore.setState((current) => ({
                  ...current,
                  token: result.data?.token || current.token,
                  isAuthenticated: true
                }));
                localStorage.setItem('auth_token', result.data.token);
              } else {
                // Don't force logout on refresh failure (endpoint/network issues).
                // Keep current token and let subsequent 401/403 responses decide.
                useAuthStore.setState((current) => ({
                  ...current,
                  isAuthenticated: !!current.token && !!current.user
                }));
              }
            })
            .catch(() => {
              // Same idea: don't kick the user due to transient failures.
              useAuthStore.setState((current) => ({
                ...current,
                isAuthenticated: !!current.token && !!current.user
              }));
            });
        } else {
          localStorage.removeItem('auth_token');
          tokenRefreshManager.stop();
        }
      },
    }
  )
);

// Selectors for easier access
export const useAuth = () => useAuthStore((state) => ({
  user: state.user,
  token: state.token,
  isAuthenticated: state.isAuthenticated,
  isLoading: state.isLoading,
}));

export const useAuthActions = () => useAuthStore((state) => ({
  setUser: state.setUser,
  setToken: state.setToken,
  setLoading: state.setLoading,
  login: state.login,
  logout: state.logout,
  updateUser: state.updateUser,
}));
